import React from 'react';
import type { HtmlSplitComponent as HtmlSplitComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getFontFamily } from '../utils/fontLoader';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { HtmlEmbedComponent } from './HtmlEmbedComponent';
import type { HtmlEmbedData } from './HtmlEmbedComponent/types';

interface HtmlSplitComponentProps {
  component: HtmlSplitComponentType;
}

export const HtmlSplitComponent: React.FC<HtmlSplitComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);
  const brandFonts = useBrandFonts();

  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  const componentId = `html-split-${component.id}`;

  const titleSizeClasses = {
    small: 'text-3xl md:text-4xl',
    medium: 'text-4xl md:text-5xl',
    large: 'text-5xl md:text-6xl lg:text-7xl',
  };

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const isGradient = (bg?: string | null) =>
    !!bg && (bg.startsWith('linear-gradient') || bg.startsWith('radial-gradient'));

  const resolvedBackground =
    data.customColors?.background ||
    themeStyles.htmlSplit?.background ||
    data.backgroundColor ||
    '#ffffff';

  const getSectionStyle = (): React.CSSProperties => {
    if (isGradient(resolvedBackground)) {
      return { backgroundImage: resolvedBackground };
    }
    return { backgroundColor: resolvedBackground };
  };

  const titleColor = data.customColors?.title || themeStyles.htmlSplit?.titleColor || '#111827';
  const subtitleColor =
    data.customColors?.subtitle ||
    themeStyles.htmlSplit?.subtitleColor ||
    data.textColor ||
    '#6b7280';
  const taglineColor =
    data.customColors?.tagline || themeStyles.htmlSplit?.taglineColor || '#3b82f6';

  const getButtonStyles = (style: string) => {
    const buttonBg = data.customColors?.buttonBg || themeStyles.htmlSplit?.buttonBg || '#3b82f6';
    const buttonText =
      data.customColors?.buttonText || themeStyles.htmlSplit?.buttonText || '#ffffff';

    switch (style) {
      case 'primary':
        return { backgroundColor: buttonBg, color: buttonText, border: 'none' };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'ghost':
        return { backgroundColor: 'transparent', color: buttonBg, border: 'none' };
      default:
        return { backgroundColor: buttonBg, color: buttonText, border: 'none' };
    }
  };

  const isHtmlLeft = data.layout === 'html-left';
  const stackOnMobile = data.stackOnMobile !== false;
  const textWidth = data.textWidth ?? 50;
  const htmlWidth = data.htmlWidth ?? 50;
  const htmlMinHeight = Math.max(200, data.htmlMinHeight ?? 480);

  const containerClasses = stackOnMobile
    ? 'flex flex-col md:flex-row items-stretch'
    : 'flex flex-row items-stretch';

  const getMobileOrder = (isText: boolean) => {
    if (!stackOnMobile) return '';
    if (data.mobileHtmlFirst) {
      return isText ? 'order-2' : 'order-1';
    }
    return isText ? 'order-1' : 'order-2';
  };

  const getDesktopOrder = (isText: boolean) => {
    if (isHtmlLeft) {
      return isText ? 'md:order-2' : 'md:order-1';
    }
    return isText ? 'md:order-1' : 'md:order-2';
  };

  const textOrder = `${getMobileOrder(true)} ${getDesktopOrder(true)}`;
  const htmlOrder = `${getMobileOrder(false)} ${getDesktopOrder(false)}`;

  const responsiveStyles = stackOnMobile
    ? `
      @media (min-width: 768px) {
        #${componentId} .html-split-text {
          width: ${textWidth}% !important;
          flex: 0 0 ${textWidth}% !important;
        }
        #${componentId} .html-split-embed {
          width: ${htmlWidth}% !important;
          flex: 0 0 ${htmlWidth}% !important;
        }
      }
    `
    : `
      #${componentId} .html-split-text {
        width: ${textWidth}%;
        flex: 0 0 ${textWidth}%;
      }
      #${componentId} .html-split-embed {
        width: ${htmlWidth}%;
        flex: 0 0 ${htmlWidth}%;
      }
    `;

  const embedData = (data.embed || {
    embedType: 'custom',
    custom: { html: '' },
  }) as Partial<HtmlEmbedData>;

  const embedComponent = {
    id: `${component.id}-embed`,
    type: 'html-embed' as const,
    data: embedData,
    order: 0,
    visible: true,
  };

  return (
    <section id={componentId} className="relative overflow-hidden" style={getSectionStyle()}>
      <style>{responsiveStyles}</style>
      <div className={containerClasses}>
        <div
          className={`html-split-text ${textOrder} flex items-center justify-center`}
          style={{
            backgroundColor: 'transparent',
            padding: '3rem 2.5rem',
            boxShadow: isHtmlLeft ? '4px 0 6px rgba(0,0,0,0.1)' : '-4px 0 6px rgba(0,0,0,0.1)',
            minHeight: `${htmlMinHeight}px`,
          }}
        >
          <div className={`w-full max-w-2xl ${alignmentClasses[data.alignment]}`}>
            {data.tagline && (
              <p
                className="opacity-80 text-sm md:text-base font-medium uppercase tracking-wide mb-4"
                style={{ color: taglineColor }}
                dangerouslySetInnerHTML={{
                  __html: renderTextWithLineBreaks(data.tagline),
                }}
              />
            )}

            <h2
              className={`${titleSizeClasses[data.size]} font-bold mb-6 leading-tight`}
              style={{ color: titleColor, whiteSpace: 'normal', ...headingFontStyle }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.title),
              }}
            />

            {data.subtitle && (
              <div
                className="text-lg md:text-xl opacity-90 mb-8 leading-relaxed rich-text-content"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{
                  __html: /<[a-z][\s\S]*>/i.test(data.subtitle)
                    ? data.subtitle
                    : renderTextWithLineBreaks(data.subtitle),
                }}
              />
            )}

            {data.buttons && data.buttons.length > 0 && (
              <div
                className={`flex flex-col sm:flex-row gap-4 ${
                  data.alignment === 'center'
                    ? 'justify-center'
                    : data.alignment === 'right'
                      ? 'justify-end'
                      : 'justify-start'
                }`}
              >
                {data.buttons.map((button, index) => (
                  <ButtonLink
                    key={index}
                    url={button.url}
                    target={button.target}
                    className="px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block text-center"
                    style={getButtonStyles(button.style || 'primary')}
                  >
                    {button.text}
                  </ButtonLink>
                ))}
              </div>
            )}
          </div>
        </div>

        <div
          className={`html-split-embed ${htmlOrder} relative overflow-auto p-4 md:p-6`}
          style={{ minHeight: `${htmlMinHeight}px` }}
          data-testid="html-split-embed"
        >
          <HtmlEmbedComponent component={embedComponent} />
        </div>
      </div>
    </section>
  );
};

export default HtmlSplitComponent;
